import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from '../../hooks/auth';
import { useClearAppCachesOnMount } from '../../hooks/useClearAppCachesOnMount';
import { SetupAuth } from '../../client';
const BRAZIL_TZ = 'America/Sao_Paulo';

function formatBrazilDatetime(isoString: string): string {
  try {
    let normalized = isoString.trim();
    if (!/Z$/i.test(normalized)) {
      normalized = normalized.replace(' ', 'T').replace(/\.\d{3}$/, '') + 'Z';
    }
    const date = new Date(normalized);
    return date.toLocaleString('pt-BR', {
      timeZone: BRAZIL_TZ,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faTable, faSpinner, faCode, faTimes, faFilter, faTimesCircle, faCopy, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Editor from '@monaco-editor/react';
import { displaySuccess } from '../../utils/messageToast';

interface FormLastEditRow {
  historyId: number;
  formId: string;
  action: string;
  lastEditedAt: string;
  lastEditedByUserId: string;
  lastEditedByUserName: string | null;
  clientId: string | null;
  clientName: string | null;
}

interface HistoryDetail {
  payloadOld: object | null;
  payloadNew: object;
}

interface UserEditCountRow {
  userId: string;
  userName: string | null;
  editCount: number;
}

function actionLabel(action?: string): string {
  switch (action) {
    case "create":
      return "Criação";
    case "edit":
      return "Edição";
    case "payload":
      return "Payload";
    case "skill":
      return "Skill";
    case "tabulation":
      return "Tabulação";
    case "pendency-response":
      return "Pendencia";
    default:
      return action || "—";
  }
}

function displayRecordId(formId: string, action?: string): string {
  if (action === "tabulation") {
    return formId.replace(/^tabulation:(force|rc):/, "");
  }
  if (action === "skill") {
    return "";
  }
  return formId;
}

export default function Metrics() {
  useClearAppCachesOnMount();
  const { authorizationRA, authorizationForce, user } = useAuthContext();
  const token = user?.inForce ? authorizationForce : authorizationRA;
  const metricsClientId = user?.clientId?.trim() ?? '';

  const [formsLastEdit, setFormsLastEdit] = useState<FormLastEditRow[]>([]);
  const [editsByUser, setEditsByUser] = useState<UserEditCountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payloadModalOpen, setPayloadModalOpen] = useState(false);
  const [payloadModalTab, setPayloadModalTab] = useState<'antes' | 'depois' | 'campos'>('antes');
  const [payloadDetail, setPayloadDetail] = useState<HistoryDetail | null>(null);
  const [payloadDetailLoading, setPayloadDetailLoading] = useState(false);

  const [filterFormId, setFilterFormId] = useState('');
  const [filterEditedBy, setFilterEditedBy] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const decorationIdsAntesRef = useRef<string[]>([]);
  const decorationIdsDepoisRef = useRef<string[]>([]);

  const loadMetrics = (overridePage?: number) => {
    if (!token) {
      setLoading(false);
      return;
    }
    const currentPage = overridePage ?? page;
    if (overridePage != null) setPage(overridePage);
    setLoading(true);
    setError(null);
    const api = SetupAuth(token);
    const params: Record<string, string | number> = { page: currentPage, pageSize };
    if (metricsClientId) params.metricsClientId = metricsClientId;
    if (filterFormId.trim()) params.formId = filterFormId.trim();
    if (filterEditedBy.trim()) params.editedBy = filterEditedBy.trim();
    if (filterClient.trim()) params.client = filterClient.trim();
    api
      .get('/metrics', { params })
      .then((res) => {
        setFormsLastEdit(res.data?.formsLastEdit ?? []);
        setTotal(res.data?.total ?? 0);
        setEditsByUser(res.data?.editsByUser ?? []);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || 'Erro ao carregar métricas.');
      })
      .finally(() => setLoading(false));
  };

  const handleApplyFilters = () => loadMetrics(1);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    loadMetrics();
  }, [token, metricsClientId]);


  const handleClearFilters = () => {
    setFilterFormId('');
    setFilterEditedBy('');
    setFilterClient('');
    setPage(1);
    if (token) {
      setLoading(true);
      setError(null);
      const api = SetupAuth(token);
      api
        .get('/metrics', {
          params: {
            page: 1,
            pageSize,
            ...(metricsClientId ? { metricsClientId } : {}),
          },
        })
        .then((res) => {
          setFormsLastEdit(res.data?.formsLastEdit ?? []);
          setTotal(res.data?.total ?? 0);
          setEditsByUser(res.data?.editsByUser ?? []);
        })
        .catch((err) => {
          setError(err?.response?.data?.error || 'Erro ao carregar métricas.');
        })
        .finally(() => setLoading(false));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const goToPage = (p: number) => {
    const next = Math.max(1, Math.min(p, totalPages));
    setPage(next);
    if (!token) return;
    setLoading(true);
    setError(null);
    const api = SetupAuth(token);
    const params: Record<string, string | number> = { page: next, pageSize };
    if (metricsClientId) params.metricsClientId = metricsClientId;
    if (filterFormId.trim()) params.formId = filterFormId.trim();
    if (filterEditedBy.trim()) params.editedBy = filterEditedBy.trim();
    if (filterClient.trim()) params.client = filterClient.trim();
    api
      .get('/metrics', { params })
      .then((res) => {
        setFormsLastEdit(res.data?.formsLastEdit ?? []);
        setTotal(res.data?.total ?? 0);
        setEditsByUser(res.data?.editsByUser ?? []);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || 'Erro ao carregar métricas.');
      })
      .finally(() => setLoading(false));
  };

  const maxEdits = editsByUser.length ? Math.max(...editsByUser.map((u) => u.editCount)) : 1;

  const openPayloadModal = (historyId: number) => {
    setPayloadModalOpen(true);
    setPayloadDetail(null);
    setPayloadModalTab('antes');
    setPayloadDetailLoading(true);
    const api = SetupAuth(token!);
    api
      .get(`/metrics/history/${historyId}`)
      .then((res) => {
        setPayloadDetail({
          payloadOld: res.data?.payloadOld ?? null,
          payloadNew: res.data?.payloadNew ?? {},
        });
      })
      .catch(() => setPayloadDetail(null))
      .finally(() => setPayloadDetailLoading(false));
  };

  const closePayloadModal = () => {
    setPayloadModalOpen(false);
    setPayloadDetail(null);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      displaySuccess('Copiado!');
    } catch {
      displaySuccess('Falha ao copiar.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Métricas</title>
      </Helmet>
      <div className="metrics-page">
        <div className="metrics-header">
          <h1 className="metrics-title">
            <FontAwesomeIcon icon={faChartLine} className="metrics-title-icon" />
            Métricas
          </h1>
          <p className="metrics-subtitle">
            Formulários criados/editados, tabulação em massa, criação de skills e responder pendências em massa.
          </p>
        </div>

        {loading && (
          <div className="metrics-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>Carregando métricas...</span>
          </div>
        )}

        {error && (
          <div className="metrics-error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <section className="metrics-section">
              <h2 className="metrics-section-title">
                <FontAwesomeIcon icon={faChartLine} />
                Edições por usuário
              </h2>
              <p className="metrics-section-desc">
                Contagem apenas para o cliente da sessão atual ({user?.clientName || metricsClientId || '—'}).
              </p>
              <div className="metrics-chart">
                {!metricsClientId ? (
                  <p className="metrics-empty">Cliente não identificado na sessão; não é possível exibir edições por usuário.</p>
                ) : editsByUser.length === 0 ? (
                  <p className="metrics-empty">Nenhuma edição registrada para este cliente ainda.</p>
                ) : (
                  <ul className="metrics-chart-list">
                    {editsByUser.map((row) => (
                      <li key={row.userId} className="metrics-chart-row">
                        <div className="metrics-chart-label">
                          <span className="metrics-chart-name">{row.userName || row.userId || '—'}</span>
                          <span className="metrics-chart-count">
                            {row.editCount === 1 ? '1 Edição' : `${row.editCount} edições`}
                          </span>
                        </div>
                        <div className="metrics-chart-bar-wrap">
                          <div
                            className="metrics-chart-bar"
                            style={{ width: `${(row.editCount / maxEdits) * 100}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className="metrics-section">
              <h2 className="metrics-section-title">
                <FontAwesomeIcon icon={faTable} />
                Formulários — último editor
              </h2>
              <p className="metrics-section-desc">
                Controle de quem editou por último cada formulário, filtrado pelo cliente da sessão atual ({user?.clientName || metricsClientId || '—'}).
              </p>
              <div className="metrics-filters">
                <div className="metrics-filter-group">
                  <label htmlFor="metrics-filter-form-id">ID do formulário</label>
                  <input
                    id="metrics-filter-form-id"
                    type="text"
                    className="metrics-filter-input"
                    placeholder="Filtrar por ID..."
                    value={filterFormId}
                    onChange={(e) => setFilterFormId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  />
                </div>
                <div className="metrics-filter-group">
                  <label htmlFor="metrics-filter-edited-by">Editado por</label>
                  <input
                    id="metrics-filter-edited-by"
                    type="text"
                    className="metrics-filter-input"
                    placeholder="Nome ou ID do usuário..."
                    value={filterEditedBy}
                    onChange={(e) => setFilterEditedBy(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  />
                </div>
                <div className="metrics-filter-group">
                  <label htmlFor="metrics-filter-client">Cliente</label>
                  <input
                    id="metrics-filter-client"
                    type="text"
                    className="metrics-filter-input"
                    placeholder="Nome ou ID do cliente..."
                    value={filterClient}
                    onChange={(e) => setFilterClient(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  />
                </div>
                <div className="metrics-filter-actions">
                  <button
                    type="button"
                    className="metrics-btn-filter"
                    onClick={handleApplyFilters}
                    title="Aplicar filtros"
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    Filtrar
                  </button>
                  <button
                    type="button"
                    className="metrics-btn-clear-filters"
                    onClick={handleClearFilters}
                    title="Limpar filtros"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} />
                    Limpar
                  </button>
                </div>
              </div>
              <div className="metrics-table-wrap">
                {formsLastEdit.length === 0 ? (
                  <p className="metrics-empty">Nenhum formulário com histórico de edição.</p>
                ) : (
                  <table className="metrics-table">
                    <thead>
                      <tr>
                        <th>ID formulário / ID tabulação</th>
                        <th>Tipo</th>
                        <th>Última edição em</th>
                        <th>Editado por</th>
                        <th>Cliente</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formsLastEdit.map((row) => (
                        <tr key={row.formId}>
                          <td className="metrics-table-id">{displayRecordId(row.formId, row.action)}</td>
                          <td>{actionLabel(row.action)}</td>
                          <td>
                            {row.lastEditedAt
                              ? formatBrazilDatetime(row.lastEditedAt)
                              : "—"}
                          </td>
                          <td>{row.lastEditedByUserName || row.lastEditedByUserId || "—"}</td>
                          <td>{row.clientName || row.clientId || "—"}</td>
                          <td>
                            <button
                              type="button"
                              className="metrics-btn-payload"
                              onClick={() => openPayloadModal(row.historyId)}
                              title="Ver payload antes e depois da edição"
                            >
                              <FontAwesomeIcon icon={faCode} />
                              Ver payloads
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {total > 0 && (
                <div className="metrics-pagination">
                  <span className="metrics-pagination-info">
                    Mostrando {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} de {total}
                  </span>
                  <div className="metrics-pagination-buttons">
                    <button
                      type="button"
                      className="metrics-pagination-btn"
                      disabled={page <= 1 || loading}
                      onClick={() => goToPage(page - 1)}
                      title="Página anterior"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <span className="metrics-pagination-page">
                      Página {page} de {totalPages}
                    </span>
                    <button
                      type="button"
                      className="metrics-pagination-btn"
                      disabled={page >= totalPages || loading}
                      onClick={() => goToPage(page + 1)}
                      title="Próxima página"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              )}
            </section>

            {payloadModalOpen && (
              <div className="metrics-modal-overlay" onClick={closePayloadModal} role="dialog" aria-modal="true">
                <div className="metrics-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="metrics-modal-header">
                    <h3>Payload antes / depois da edição</h3>
                    <button type="button" className="metrics-modal-close" onClick={closePayloadModal} aria-label="Fechar">
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  <div className="metrics-modal-tabs">
                    <button
                      type="button"
                      className={payloadModalTab === 'antes' ? 'is-active' : ''}
                      onClick={() => setPayloadModalTab('antes')}
                    >
                      Payload antes
                    </button>
                    <button
                      type="button"
                      className={payloadModalTab === 'depois' ? 'is-active' : ''}
                      onClick={() => setPayloadModalTab('depois')}
                    >
                      Payload depois
                    </button>
                  </div>
                  <div className="metrics-modal-body">
                    {payloadDetailLoading && (
                      <div className="metrics-loading">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Carregando...</span>
                      </div>
                    )}
                    {!payloadDetailLoading && payloadDetail && payloadModalTab === 'antes' && (
                      <div className="metrics-modal-editor">
                        <div className="metrics-modal-copy-bar">
                          <button
                            type="button"
                            className="metrics-btn-copy"
                            onClick={() =>
                              copyToClipboard(
                                payloadDetail.payloadOld == null
                                  ? 'Sem payload anterior (primeira edição ou formulário novo).'
                                  : JSON.stringify(payloadDetail.payloadOld, null, 2)
                              )
                            }
                            title="Copiar conteúdo"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                            Copiar
                          </button>
                        </div>
                        {payloadDetail.payloadOld == null ? (
                          <p className="metrics-empty">Sem payload anterior (primeira edição ou formulário novo).</p>
                        ) : (
                          <Editor
                            height="400px"
                            language="json"
                            theme="vs-light"
                            value={JSON.stringify(payloadDetail.payloadOld, null, 2)}
                            options={{ readOnly: true, minimap: { enabled: false }, scrollBeyondLastLine: false }}
                            onMount={(editor) => {
                              decorationIdsAntesRef.current = editor.deltaDecorations(
                                decorationIdsAntesRef.current,
                                []
                              );
                            }}
                          />
                        )}
                      </div>
                    )}
                    {!payloadDetailLoading && payloadDetail && payloadModalTab === 'depois' && (
                      <div className="metrics-modal-editor">
                        <div className="metrics-modal-copy-bar">
                          <button
                            type="button"
                            className="metrics-btn-copy"
                            onClick={() => copyToClipboard(JSON.stringify(payloadDetail.payloadNew, null, 2))}
                            title="Copiar conteúdo"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                            Copiar
                          </button>
                        </div>
                        <Editor
                          height="400px"
                          language="json"
                          theme="vs-light"
                          value={JSON.stringify(payloadDetail.payloadNew, null, 2)}
                          options={{ readOnly: true, minimap: { enabled: false }, scrollBeyondLastLine: false }}
                          onMount={(editor) => {
                            decorationIdsDepoisRef.current = editor.deltaDecorations(
                              decorationIdsDepoisRef.current,
                              []
                            );
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
